import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, ThemeProvider, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';
import { useLazyQuery } from '@apollo/client';

import {
  ContentContainer,
  HeaderBox,
  Title,
  getResendButtonStyles,
} from './SmsVerify.styles';

import { OtpWithResend } from '@/pages/Onboarding/components/OtpInputGroup/OtpWithResend';
import sparkTheme from '@/themes/sparkTheme';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';

import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { logError } from '@/utils/logError';
import { getEntitlementStatus } from '@/utils/entitlementUtils';

interface SmsVerifyLocationState {
  phoneNumber?: string;
  marketingConsent?: boolean;
  limbixUserId?: string;
  accessCode?: string;
  fromLogin?: boolean;
  enrollment?: string;
  orgSlug?: string;
}

const SmsVerifyInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const theme = useTheme();
  const posthog = usePostHog();
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { state: accountData } = useLocation<SmsVerifyLocationState>();
  const fromLogin = accountData?.fromLogin ?? false;

  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const currentOtpRef = useRef('');
  const [fetchMe] = useLazyQuery(ME_QUERY, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    posthog?.capture('SparkSmsVerifyScreenViewed');
    if (fromLogin) {
      posthog?.capture('LoginOTPLanded');
    } else {
      posthog?.capture('SignUpOTPLanded', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
      });
    }
  }, [posthog, fromLogin, accountData?.phoneNumber]);

  const handleOtpComplete = useCallback(async (token: string) => {
    if (!accountData?.phoneNumber) { return; }

    setIsVerifying(true);
    setError('');

    posthog?.capture('SparkSmsVerifyCodeEntered');
    if (fromLogin) {
      posthog?.capture('LoginOTPSubmitted', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: token,
      });
    } else {
      posthog?.capture('SignUpOTPSubmitted', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: token,
      });
    }

    const { data: verifyData, error: verifyError } = await getSupabaseClient().auth.verifyOtp({
      phone: accountData.phoneNumber,
      token,
      type: 'sms',
    });

    if (verifyError) {
      if (fromLogin) {
        posthog?.capture('LoginOTPSubmittedFailed', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
          otp: token,
        });
      } else {
        posthog?.capture('SignUpOTPFailed', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
          otp: token,
        });
      }
      setError(t('coverage.pre_onboarding.sms_verify.error_invalid'));
      setIsVerifying(false);
      return;
    }

    const supabaseUserId = verifyData?.user?.id;
    posthog?.capture('SparkSmsVerifySuccess', { supabase_user_id: supabaseUserId });
    if (fromLogin) {
      posthog?.capture('LoginOTPSubmittedSucceeded', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: token,
      });
    } else {
      posthog?.capture('SignUpOTPSucceeded', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: token,
      });
    }

    if (!fromLogin) {
      try {
        await activateSparkConfirm({
          supabaseUserId: supabaseUserId ?? '',
          accessToken: verifyData?.session?.access_token ?? '',
          phDistinctId: posthog?.get_distinct_id(),
        });
      } catch (err) {
        logError('[SmsVerify]', 'activation failed', err);
        setError(t('coverage.errors.generic_error'));
        setIsVerifying(false);
        return;
      }
      if (accountData?.orgSlug) {
        history.push(`/activate/${product}/coverage-check/${accountData.orgSlug}`, {
          patientUid: accountData?.limbixUserId,
          phoneNumber: accountData?.phoneNumber,
          accessToken: verifyData?.session?.access_token,
        });
      } else {
        history.push(`/activate/${product}/user-info/confirmation`, {
          accessCode: accountData?.accessCode,
          phoneNumber: accountData?.phoneNumber,
          patientUid: accountData?.limbixUserId,
        });
      }
      return;
    }

    const entitlementStatus = getEntitlementStatus(verifyData?.session?.access_token ?? '');
    if (entitlementStatus !== 'active') {
      try {
        await activateSparkConfirm({
          supabaseUserId: supabaseUserId ?? '',
          accessToken: verifyData?.session?.access_token ?? '',
          phDistinctId: posthog?.get_distinct_id(),
        });
      } catch (err) {
        logError('[SmsVerify]', 'activation failed', err);
        setError(t('coverage.errors.generic_error'));
        setIsVerifying(false);
        return;
      }
    }

    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await getSupabaseClient().auth.refreshSession();
    if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
      logError('[SmsVerify]', 'refreshSession failed', refreshError ?? new Error('no session'));
      setError(t('coverage.errors.generic_error'));
      setIsVerifying(false);
      return;
    }

    const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

    if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
      history.push(`/activate/${product}/download-app`);
      return;
    }

    let exchangeToken: string;
    try {
      ({ exchangeToken } = await requestAuthCode(
        refreshedSession.access_token,
        refreshedSession.refresh_token,
      ));
    } catch (err) {
      logError('[SmsVerify]', 'requestAuthCode failed', err);
      setError(t('coverage.errors.generic_error'));
      setIsVerifying(false);
      return;
    }

    const { data: meData } = await fetchMe({
      context: { headers: { Authorization: `Bearer ${refreshedSession.access_token}` } },
    });
    const limbixUserUid: string | undefined = meData?.me?.uid ?? undefined;

    const deepLinkUrl = new URL(redirectTo);
    deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
    if (limbixUserUid) {
      deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
    }

    window.location.href = deepLinkUrl.toString();
  }, [accountData, product, history, posthog, t, fetchMe, fromLogin]);

  const handleResend = async () => {
    if (!accountData?.phoneNumber) { return; }

    posthog?.capture('SparkSmsVerifyResendClicked');
    if (fromLogin) {
      posthog?.capture('LoginOTPRequested', {
        program: 'spark',
        phone_number: accountData.phoneNumber,
        input: accountData.phoneNumber,
        otp: currentOtpRef.current,
      });
    }

    const { error: resendError } = await getSupabaseClient().auth.signInWithOtp({
      phone: accountData.phoneNumber,
    });

    if (resendError) {
      logError('[SmsVerify]', 'resend signInWithOtp failed', resendError);
      throw new Error(resendError.message);
    }

    if (fromLogin) {
      posthog?.capture('LoginOTPSent', {
        phone_number: accountData.phoneNumber,
        input: accountData.phoneNumber,
      });
    }
  };

  return (
    <ContentContainer>
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sms_verify.title')}
          </Title>
          <Typography color="textPrimary">
            {t(fromLogin ? 'coverage.spark.login.otp_sub_title' : 'coverage.pre_onboarding.sms_verify.sub_title')}
          </Typography>
        </HeaderBox>

        <OtpWithResend
          onComplete={handleOtpComplete}
          onResend={handleResend}
          onChange={(value) => { currentOtpRef.current = value; }}
          errorText={error}
          disabled={isVerifying}
          resendButtonStyle={getResendButtonStyles(theme)}
        />

        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const SmsVerify: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <SmsVerifyInner />
  </ThemeProvider>
);

export default SmsVerify;
